import * as React from 'react';

import type { NodeChange, NodePositionChange } from '@xyflow/react';

import { getHelperLines, SNAP_THRESHOLD } from '../helpers/helperLinesUtils';
import type { AutomationNode } from '../nodes/types';

export function useHelperLines(
  enabled = true,
  threshold: number = SNAP_THRESHOLD,
) {
  const [helperLines, setHelperLines] = React.useState<{
    horizontal: number | null;
    vertical: number | null;
  }>({ horizontal: null, vertical: null });

  // Cache snapped positions during drag to apply on drop
  const lastSnappedPositions = React.useRef<
    Map<string, { x: number; y: number }>
  >(new Map());

  const processNodesChange = React.useCallback(
    (changes: NodeChange[], nodes: AutomationNode[]): NodeChange[] => {
      if (!enabled) {
        // Clear helper lines and cache if disabled
        setHelperLines({ horizontal: null, vertical: null });
        lastSnappedPositions.current.clear();
        return changes;
      }

      // Check for drag end events (dragging: false)
      const dragEndChanges = changes.filter(
        (change): change is NodePositionChange =>
          change.type === 'position' && change.dragging === false,
      );

      // Handle drag end - apply cached snapped positions
      if (dragEndChanges.length > 0) {
        setHelperLines({ horizontal: null, vertical: null });

        // Apply cached snapped positions to drag end changes
        const modifiedChanges = changes.map((change) => {
          if (change.type === 'position' && change.dragging === false) {
            const cached = lastSnappedPositions.current.get(change.id);
            if (cached) {
              lastSnappedPositions.current.delete(change.id);
              return { ...change, position: cached };
            }
          }
          return change;
        });

        return modifiedChanges;
      }

      // Find position changes that are dragging
      const positionChanges = changes.filter(
        (change): change is NodePositionChange =>
          change.type === 'position' && change.dragging === true,
      );

      // If no dragging changes, clear helper lines
      if (positionChanges.length === 0) {
        setHelperLines({ horizontal: null, vertical: null });
        return changes;
      }

      // Process each dragging change
      const modifiedChanges = changes.map((change) => {
        if (
          change.type !== 'position' ||
          !change.dragging ||
          change.position === undefined
        ) {
          return change;
        }

        // Find the node being dragged
        const draggingNode = nodes.find((n) => n.id === change.id);
        if (!draggingNode) return change;

        // Create a temporary node with the new position
        const tempNode: AutomationNode = {
          ...draggingNode,
          position: change.position,
        };

        // Get all other nodes (excluding the one being dragged)
        const otherNodes = nodes.filter((n) => n.id !== change.id);

        // Calculate helper lines and snap position
        const result = getHelperLines(tempNode, otherNodes, threshold);

        // Cache snapped position for use on drop
        lastSnappedPositions.current.set(change.id, result.snapPosition);

        // Update helper lines state (only for the first dragging node)
        if (change.id === positionChanges[0].id) {
          setHelperLines({
            horizontal: result.horizontal,
            vertical: result.vertical,
          });
        }

        // Return modified change with snapped position
        return {
          ...change,
          position: result.snapPosition,
        };
      });

      return modifiedChanges;
    },
    [enabled, threshold],
  );

  // Clear helper lines when disabled
  React.useEffect(() => {
    if (!enabled) {
      setHelperLines({ horizontal: null, vertical: null });
    }
  }, [enabled]);

  return { helperLines, processNodesChange };
}
